/**
 * 5D Cost Modelling Page
 * Full replica of 4D Modelling but links 3D objects to CBS cost codes
 * Visualization modes: Budget vs Actual, Cost Distribution, EVM Objects, Cost Timeline
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { authFetch } from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { usePermissions } from '@/contexts/PermissionContext';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { toast } from 'sonner';
import {
  Upload, Link2, Wand2, RefreshCw, Maximize2, Eye,
  Box, DollarSign, AlertTriangle, CheckCircle2,
  ChevronsUp, ChevronsDown, ArrowLeft, Trash2, Loader2, Gauge
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import FourDViewer from '@/components/fourd/FourDViewer';
import { ObjectTreePanel } from '@/components/fourd/ObjectTreePanel';
import { buildObjectTree, findAncestors } from '@/components/fourd/ObjectTree';
import { CostGrid } from '@/components/fived/CostGrid';
import { CostLinkingPanel } from '@/components/fived/CostLinkingPanel';
import { FiveDTimeliner } from '@/components/fived/FiveDTimeliner';
import { CostLegend } from '@/components/fived/CostLegend';
import FiveDImpactAnalyst from '@/components/fived/FiveDImpactAnalyst';

import { useProject } from '@/contexts/ProjectContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FiveDModelling() {
  const navigate = useNavigate();
  const { projectId: PROJECT_ID } = useProject();

  // Compact currency formatter for the KPI overlay (K / L / Cr).
  const fmtCr = (v) => {
    if (!v && v !== 0) return '—';
    const a = Math.abs(v);
    if (a >= 10000000) return `${(v / 10000000).toFixed(2)} Cr`;
    if (a >= 100000)   return `${(v / 100000).toFixed(1)} L`;
    if (a >= 1000)     return `${(v / 1000).toFixed(1)} K`;
    return v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };
  const { can: canPerm } = usePermissions();
  const can5DCreate = canPerm('VPR', 'create');
  const can5DEdit = canPerm('VPR', 'edit');

  // Models & data
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [mappings, setMappings] = useState([]);

  // Timeliner
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotIndex, setSnapshotIndex] = useState(0);
  const [currentSnapshot, setCurrentSnapshot] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(0.5);
  const playRef = useRef(null);

  // Viz
  const [vizMode, setVizMode] = useState('BUDGET_VS_ACTUAL');
  const [showLegend, setShowLegend] = useState(true);
  const [showKpiOverlay, setShowKpiOverlay] = useState(true);

  // UI
  const [selectedCostCodeDbIds, setSelectedCostCodeDbIds] = useState([]);
  const [selectedCostCode, setSelectedCostCode] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [bottomTab, setBottomTab] = useState('cost-codes');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gridSearchText, setGridSearchText] = useState('');
  const [importingMapping, setImportingMapping] = useState(false);
  const [timelinerCollapsed, setTimelinerCollapsed] = useState(false);
  const [hotspotMode, setHotspotMode] = useState(null); // 'COST_CREEP' | 'SPI' | 'GRADIENT_*' | null
  const [hotspotCounts, setHotspotCounts] = useState(null);
  const [viewerReady, setViewerReady] = useState(false);

  // Poll the imperative viewer ref until it exposes applyHotspot — otherwise
  // the buttons silently no-op because the 3D viewer hasn't loaded yet.
  useEffect(() => {
    if (viewerReady) return;
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      if (typeof viewerRef.current?.applyHotspot === 'function') {
        setViewerReady(true);
      } else {
        setTimeout(check, 500);
      }
    };
    const t = setTimeout(check, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [selectedModelId, viewerReady]);

  const toggleHotspots = useCallback(async (nextMode) => {
    if (!selectedModelId) return;
    // Toggling off: clear
    if (hotspotMode === nextMode) {
      setHotspotMode(null); setHotspotCounts(null);
      try { viewerRef.current?.clearHotspot?.(); } catch {}
      toast.info('Hotspots cleared');
      return;
    }
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/hotspots?mode=${nextMode}`);
      const d = await r.json();
      const counts = { red: 0, amber: 0, green: 0, grey: 0 };
      (d.hotspots || []).forEach(h => {
        if (h.color === '#EF4444') counts.red++;
        else if (h.color === '#F59E0B') counts.amber++;
        else if (h.color === '#22C55E') counts.green++;
        else counts.grey++;
      });
      setHotspotMode(nextMode); setHotspotCounts(counts);
      try { viewerRef.current?.applyHotspot?.(d.hotspots); } catch {}
      toast.success(`3D Hotspot: ${counts.red} red · ${counts.amber} amber · ${counts.green} green (${nextMode})`);
    } catch { toast.error('Hotspot fetch failed'); }
  }, [selectedModelId, hotspotMode, PROJECT_ID]);

  // Object tree
  const [flatObjectTree, setFlatObjectTree] = useState([]);
  const [objectTreeTotal, setObjectTreeTotal] = useState(0);
  const [objectTreeSearch, setObjectTreeSearch] = useState('');
  const [loadingTree, setLoadingTree] = useState(false);
  const [expandedTreeNodes, setExpandedTreeNodes] = useState(new Set());
  const [selectedTreeDbId, setSelectedTreeDbId] = useState(null);
  const [highlightedTreeDbId, setHighlightedTreeDbId] = useState(null);

  // Details
  const [selectedObjectDetail, setSelectedObjectDetail] = useState(null);
  const [selectedCostCodeDetail, setSelectedCostCodeDetail] = useState(null);

  const viewerRef = useRef(null);

  // Data Fetching
  const fetchModels = useCallback(async () => {
    if (!PROJECT_ID) return;
    try {
      const resp = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models`);
      const data = await resp.json();
      const list = data.models || [];
      setModels(list);
      if (list.length > 0 && !selectedModelId) {
        setSelectedModelId(list[0].modelId);
      }
    } catch (e) { console.error(e); }
  }, [selectedModelId, PROJECT_ID]);

  const fetchModelDetail = useCallback(async () => {
    if (!selectedModelId || !PROJECT_ID) return;
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}`);
      setSelectedModel(await r.json());
    } catch {}
  }, [selectedModelId, PROJECT_ID]);

  const fetchMappings = useCallback(async () => {
    if (!selectedModelId || !PROJECT_ID) return;
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/mappings?limit=5000`);
      const d = await r.json();
      setMappings(d.mappings || []);
    } catch {}
  }, [selectedModelId, PROJECT_ID]);

  const fetchSnapshots = useCallback(async () => {
    if (!selectedModelId || !PROJECT_ID) return;
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/timeliner/snapshots?mode=${vizMode}`);
      const d = await r.json();
      const list = d.snapshots || [];
      setSnapshots(list);
      setSnapshotIndex(0);
      // Auto-generate on first entry when the model has mappings but no snapshots yet
      if (list.length === 0) {
        const mr = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/mappings?limit=1`);
        const md = await mr.json();
        if ((md.mappings || md.total || 0) && !generating) {
          setGenerating(true);
          try {
            await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/timeliner/generate`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ modes: ['BUDGET_VS_ACTUAL', 'COST_DISTRIBUTION', 'EVM_OBJECTS', 'COST_TIMELINE'] }),
            });
            const r2 = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/timeliner/snapshots?mode=${vizMode}`);
            const d2 = await r2.json();
            setSnapshots(d2.snapshots || []);
          } catch {}
          setGenerating(false);
        }
      }
    } catch {}
  }, [selectedModelId, vizMode, PROJECT_ID, generating]);

  const fetchSnapshotDetail = useCallback(async (index) => {
    if (!selectedModelId || !PROJECT_ID) return;
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/timeliner/snapshot/${index}?mode=${vizMode}`);
      setCurrentSnapshot(await r.json());
    } catch {}
  }, [selectedModelId, vizMode, PROJECT_ID]);

  const fetchObjectTree = useCallback(async (search = '') => {
    if (!selectedModelId || !PROJECT_ID) return;
    setLoadingTree(true);
    try {
      const qs = search ? `&search=${encodeURIComponent(search)}` : '';
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/object-tree?limit=10000${qs}`);
      const d = await r.json();
      setFlatObjectTree(d.objects || []);
      setObjectTreeTotal(d.total || 0);
      if (!search) {
        const toExpand = new Set();
        for (const obj of (d.objects || [])) {
          if (obj.depth <= 2 && obj.childCount > 0) toExpand.add(obj.dbId);
        }
        setExpandedTreeNodes(toExpand);
      }
    } catch {}
    setLoadingTree(false);
  }, [selectedModelId, PROJECT_ID]);

  const objectTreeRoots = useMemo(() => buildObjectTree(flatObjectTree), [flatObjectTree]);

  // Bidirectional Navigation
  const handleViewerObjectSelect = useCallback(async (dbId) => {
    setSelectedCostCodeDetail(null);
    setHighlightedTreeDbId(dbId);
    // Model → grid: if a cost code owns this object, light up its row so the
    // CBS table always mirrors what is selected in the 3D model.
    const owner = mappings.find(m => (m.modelObjects || []).some(o => o.dbId === dbId));
    setSelectedCostCode(owner?.costCode || null);
    if (owner) setSelectedCostCodeDetail(owner);
    const ancestors = findAncestors(flatObjectTree, dbId);
    setExpandedTreeNodes(prev => {
      const next = new Set(prev);
      for (const aid of ancestors) next.add(aid);
      return next;
    });
    setTimeout(() => {
      const el = document.querySelector(`[data-dbid="${dbId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
    // Only pull the user to the object tree when no cost code claims the object.
    if (!owner && bottomTab !== 'objects') setBottomTab('objects');
    if (!selectedModelId || !PROJECT_ID) return;
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/object-tree/${dbId}`);
      if (r.ok) { setSelectedObjectDetail(await r.json()); setSelectedTreeDbId(dbId); }
    } catch {}
  }, [flatObjectTree, selectedModelId, bottomTab, PROJECT_ID, mappings]);

  const handleTreeNodeSelect = useCallback(async (dbId) => {
    setSelectedTreeDbId(dbId);
    setSelectedCostCodeDetail(null);
    setHighlightedTreeDbId(null);
    viewerRef.current?.selectAndZoom(dbId);
    if (!selectedModelId || !PROJECT_ID) return;
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/object-tree/${dbId}`);
      if (r.ok) setSelectedObjectDetail(await r.json());
      else setSelectedObjectDetail({ dbId, name: `Object ${dbId}`, properties: {} });
    } catch { setSelectedObjectDetail({ dbId, name: `Object ${dbId}`, properties: {} }); }
  }, [selectedModelId, PROJECT_ID]);

  const handleTreeToggle = useCallback((dbId) => {
    setExpandedTreeNodes(prev => {
      const next = new Set(prev);
      if (next.has(dbId)) next.delete(dbId); else next.add(dbId);
      return next;
    });
  }, []);

  const handleCostCodeSelect = useCallback((costCode) => {
    setSelectedObjectDetail(null);
    const m = mappings.find(x => x.costCode === costCode);
    setSelectedCostCodeDetail(m || null);
    setSelectedCostCode(costCode);
    // A summary row stands for its whole branch, and the rollup strips dbIds
    // off parents — so gather the geometry from the branch's leaves.
    const branch = mappings.filter(
      x => x.costCode === costCode || String(x.costCode || '').startsWith(`${costCode}.`)
    );
    const dbIds = Array.from(new Set(
      branch.flatMap(x => (x.modelObjects || []).map(o => o.dbId)).filter(v => v != null)
    ));
    setSelectedCostCodeDbIds(dbIds);
    if (!dbIds.length) {
      toast.info(`${costCode} has no 3D objects linked yet — link geometry in the Cost Linking tab.`);
    }
    // 5D → 4D cross-reference: publish the selected cost code so the 4D
    // Timeliner (in another tab / navigation) can highlight the linked
    // activity next time it's opened, keeping schedule + cost views in sync.
    try {
      const payload = {
        projectId: PROJECT_ID,
        costCode,
        costCodeName: m?.costCodeName || '',
        linkedDbIds: dbIds,
        at: Date.now(),
      };
      sessionStorage.setItem('drsti:5d-cbs-select', JSON.stringify(payload));
      window.dispatchEvent(new CustomEvent('drsti:cbs-select', { detail: payload }));
    } catch {}
  }, [mappings, PROJECT_ID]);

  // Effects
  useEffect(() => {
    if (!PROJECT_ID) return;
    setModels([]); setSelectedModelId(null); setSelectedModel(null); setMappings([]);
    setSnapshots([]); setSnapshotIndex(0); setCurrentSnapshot(null); setFlatObjectTree([]);
    authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/seed`, { method: 'POST' }).catch(() => {});
    fetchModels();
  }, [PROJECT_ID]);

  useEffect(() => {
    if (selectedModelId) { fetchModelDetail(); fetchMappings(); fetchSnapshots(); }
  }, [selectedModelId, vizMode]);

  useEffect(() => {
    if (snapshots.length > 0 && snapshotIndex < snapshots.length) fetchSnapshotDetail(snapshotIndex);
  }, [snapshotIndex, snapshots]);

  useEffect(() => {
    if ((bottomTab === 'objects' || bottomTab === 'linking') && selectedModelId && flatObjectTree.length === 0) fetchObjectTree('');
  }, [bottomTab, selectedModelId]);

  useEffect(() => {
    if (isPlaying) {
      playRef.current = setInterval(() => {
        setSnapshotIndex(prev => { if (prev >= snapshots.length - 1) { setIsPlaying(false); return prev; } return prev + 1; });
      }, 1000 / playSpeed);
    } else { clearInterval(playRef.current); }
    return () => clearInterval(playRef.current);
  }, [isPlaying, playSpeed, snapshots.length]);

  // Toolbar Actions
  const handleUpload = async (e) => {
    e.preventDefault(); setUploading(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/upload`, { method: 'POST', body: new FormData(e.target) });
      const d = await r.json();
      if (r.ok && d.modelId) { toast.success('Model uploaded!'); setSelectedModelId(d.modelId); setShowUploadDialog(false); await fetchModels(); }
      else toast.error(d.detail || 'Upload failed');
    } catch (err) { toast.error(err.message); }
    setUploading(false);
  };

  const handleLinkCbs = async () => {
    if (!selectedModelId) return; setSyncing(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/link-cbs`, { method: 'POST' });
      const d = await r.json(); toast.success(`Linked ${d.costCodesLinked} cost codes`);
      fetchMappings(); fetchModelDetail();
    } catch { toast.error('CBS link failed'); }
    setSyncing(false);
  };

  const handleAutoMap = async () => {
    if (!selectedModelId) return; setSyncing(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/mappings/auto`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ strategy: 'ALL' }),
      });
      const d = await r.json(); toast.success(`Auto-mapped: ${d.matched} objects`); fetchMappings(); fetchModelDetail();
    } catch { toast.error('Auto-map failed'); }
    setSyncing(false);
  };

  const handleSyncCbs = async () => {
    if (!selectedModelId) return; setSyncing(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/sync-cbs`, { method: 'POST' });
      const d = await r.json();
      toast.success(`Synced ${d.updated} cost codes`);
      // Auto-generate multi-period cost snapshots so the 5D Timeliner Play
      // button has ≥N weekly/monthly frames to animate through. COST_TIMELINE
      // is the only mode that produces multiple frames — force it here so
      // Play always has something to step through after the first Sync.
      try {
        const g = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/timeliner/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ modes: ['COST_TIMELINE', 'BUDGET_VS_ACTUAL', 'COST_DISTRIBUTION', 'EVM_OBJECTS'], granularity: 'WEEKLY', periods: 52 }),
        });
        if (g.ok) {
          const gd = await g.json();
          if (gd.totalSnapshots) toast.message(`Generated ${gd.totalSnapshots} cost snapshots for the Timeliner`);
        }
      } catch {}
      fetchMappings(); fetchModelDetail(); fetchSnapshots();
    } catch { toast.error('Sync failed'); } setSyncing(false);
  };

  const handleSync3dFrom4d = async () => {
    if (!selectedModelId) return; setSyncing(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/sync-3d-links-from-4d`, { method: 'POST' });
      const d = await r.json();
      if (r.ok) toast.success(`Copied 3D links: ${d.mappingsUpdated} cost codes now have objects (from ${d.activitiesMatched} activities)`);
      else toast.error(d.detail || 'Sync 3D from 4D failed');
      fetchMappings(); fetchModelDetail();
    } catch (e) { toast.error(e.message || 'Sync 3D from 4D failed'); }
    setSyncing(false);
  };

  // CBS SSOT — import macro-schedule activities as CBS elements
  const handleSyncCbsFromSchedule = async () => {
    setSyncing(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/cc/cbs/sync-from-schedule`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ strategy: 'overwrite', default_element_type: 'DIRECT_COST' }),
      });
      const d = await r.json();
      if (r.ok) toast.success(`Imported ${d.imported} activities into CBS as SSOT`);
      else toast.error(d.detail || 'CBS sync failed');
    } catch (e) { toast.error(e.message || 'CBS sync failed'); }
    setSyncing(false);
  };

  const handleGenerateSnapshots = async () => {
    if (!selectedModelId) return; setGenerating(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/timeliner/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modes: ['BUDGET_VS_ACTUAL', 'COST_DISTRIBUTION', 'EVM_OBJECTS', 'COST_TIMELINE'] }),
      });
      const d = await r.json(); toast.success(`Generated ${d.totalSnapshots} cost snapshots`); fetchSnapshots();
    } catch { toast.error('Generation failed'); }
    setGenerating(false);
  };

  const handleUnlinkAll = async () => {
    if (!selectedModelId) return; setSyncing(true);
    try {
      await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/mappings/unlink-all`, { method: 'DELETE' });
      toast.success('All object links removed'); fetchMappings(); fetchModelDetail();
    } catch { toast.error('Unlink failed'); } setSyncing(false);
  };

  const handleMappingExport = () => {
    if (!selectedModelId) return;
    const a = document.createElement('a');
    a.href = `${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/mappings/export`;
    a.download = '5D_Cost_Mappings.xlsx'; a.click();
  };

  const handleMappingTemplate = () => {
    if (!selectedModelId) return;
    const a = document.createElement('a');
    a.href = `${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/mappings/template`;
    a.download = '5D_Template.xlsx'; a.click();
  };

  const handleMappingImport = async (file) => {
    if (!selectedModelId || !file) return; setImportingMapping(true);
    try {
      const form = new FormData(); form.append('file', file);
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/5d/models/${selectedModelId}/mappings/import`, { method: 'POST', body: form });
      const d = await r.json();
      if (r.ok) { toast.success(`Imported: ${d.updated} mappings`); fetchMappings(); fetchModelDetail(); }
      else toast.error(d.detail || 'Import failed');
    } catch (err) { toast.error(err.message); }
    setImportingMapping(false);
  };

  const handleDeleteModel = async () => {
    if (!selectedModelId || !selectedModel) return;
    const name = selectedModel.modelName || selectedModelId;
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will remove all associated data (object tree, mappings, cost snapshots). This action cannot be undone.`)) return;
    try {
      const source = selectedModel._source || '5D';
      const base = source === '4D' ? '4d' : '5d';
      const r = await authFetch(`${API}/api/v1/projects/${PROJECT_ID}/${base}/models/${selectedModelId}`, { method: 'DELETE' });
      if (!r.ok) { const d = await r.json(); toast.error(d.detail || 'Delete failed'); return; }
      toast.success(`"${name}" deleted successfully`);
      setSelectedModelId(null); setSelectedModel(null); setMappings([]); setSnapshots([]);
      setCurrentSnapshot(null); setFlatObjectTree([]); setObjectTreeTotal(0);
      await fetchModels();
    } catch (err) { toast.error(err.message); }
  };

  const stats = selectedModel?.mappingStats || {};

  return (
    <DashboardLayout>
      <div className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-background' : 'h-[calc(100vh-4rem)]'}`} data-testid="fived-page">
        {/* Toolbar */}
        <FiveDToolbar
          models={models}
          selectedModelId={selectedModelId}
          setSelectedModelId={setSelectedModelId}
          selectedModel={selectedModel}
          vizMode={vizMode}
          setVizMode={setVizMode}
          showLegend={showLegend}
          setShowLegend={setShowLegend}
          showKpiOverlay={showKpiOverlay}
          setShowKpiOverlay={setShowKpiOverlay}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          syncing={syncing}
          generating={generating}
          onUpload={can5DCreate ? () => setShowUploadDialog(true) : undefined}
          onLinkCbs={can5DEdit ? handleLinkCbs : undefined}
          onAutoMap={can5DEdit ? handleAutoMap : undefined}
          onSyncCbs={handleSyncCbs}
          onSyncCbsFromSchedule={handleSyncCbsFromSchedule}
          onGenerateSnapshots={handleGenerateSnapshots}
          onBack={() => navigate('/project-controls/cost')}
          onDeleteModel={handleDeleteModel}
        />

        <ResizablePanelGroup direction="vertical" className="flex-1" data-testid="fived-resizable-layout">
          <ResizablePanel defaultSize={55} minSize={20} data-testid="fived-viewer-panel">
            <div className="relative h-full">
              <FourDViewer
                ref={viewerRef}
                modelUrn={selectedModel?.apsBucketOrphaned ? null : (selectedModel?.apsDerivativeUrn || selectedModel?.apsModelUrn)}
                translationStatus={selectedModel?.translationStatus}
                apsBucketOrphaned={selectedModel?.apsBucketOrphaned}
                hasSourceBackup={selectedModel?.hasSourceBackup}
                modelId={selectedModel?.modelId || selectedModel?._id}
                onRecovered={() => window.dispatchEvent(new CustomEvent('fourd-model-refresh'))}
                currentSnapshot={currentSnapshot}
                onObjectSelect={handleViewerObjectSelect}
                selectedDbIds={selectedCostCodeDbIds}
                viewerSettings={selectedModel?.viewerSettings}
                projectId={PROJECT_ID}
              />
              <CostLegend mode={vizMode} visible={showLegend} onToggle={() => setShowLegend(false)} />

              {/* 3D Hotspot toolbar (over-viewer) */}
              <div className="absolute top-2 left-2 flex items-center gap-1 bg-background/85 backdrop-blur-md border rounded-lg p-1 z-10 text-[10px]" data-testid="hotspot-toolbar">
                <span className="px-1 font-semibold" style={{ color: 'var(--text-muted)' }}>Hotspot:</span>
                <Button size="sm" variant={hotspotMode === 'COST_CREEP' ? 'default' : 'ghost'} className="h-6 px-2 text-[10px]"
                  onClick={() => toggleHotspots('COST_CREEP')} disabled={!viewerReady} title={!viewerReady ? 'Waiting for 3D viewer to load…' : ''}
                  data-testid="hotspot-cost-btn">
                  Cost Creep
                </Button>
                <Button size="sm" variant={hotspotMode === 'SPI' ? 'default' : 'ghost'} className="h-6 px-2 text-[10px]"
                  onClick={() => toggleHotspots('SPI')} disabled={!viewerReady} title={!viewerReady ? 'Waiting for 3D viewer to load…' : ''}
                  data-testid="hotspot-spi-btn">
                  SPI
                </Button>
                {!viewerReady && <span style={{ color: 'var(--text-muted)' }}>⏳</span>}
                {hotspotCounts && viewerReady && (
                  <div className="flex items-center gap-1 pl-1 border-l" style={{ borderColor: 'var(--border-color)' }}>
                    <span style={{ color: '#EF4444' }}>{hotspotCounts.red}</span>·
                    <span style={{ color: '#F59E0B' }}>{hotspotCounts.amber}</span>·
                    <span style={{ color: '#22C55E' }}>{hotspotCounts.green}</span>
                  </div>
                )}
              </div>

              {/* Hotspot Legend — floating (bottom-left of viewer). Only shown
                  when a hotspot mode is active so it doesn't clutter the
                  viewer at rest. Explains the RAG palette and the "faded =
                  unlinked" convention introduced by applyHotspot's low-alpha
                  wash on non-hotspot objects. */}
              {hotspotMode && viewerReady && (
                <div className="absolute bottom-2 left-2 bg-background/90 backdrop-blur-md border rounded-lg p-2 text-[10px] z-10 shadow-md" data-testid="hotspot-legend">
                  <div className="uppercase tracking-wider font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>
                    Legend — {hotspotMode.replace('_', ' ')}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#EF4444', opacity: 0.85 }} />
                      <span style={{ color: 'var(--text-primary)' }}>Red — {hotspotMode.includes('COST') ? 'Cost overrun ≥ 15%' : 'SPI < 0.85'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#F59E0B', opacity: 0.85 }} />
                      <span style={{ color: 'var(--text-primary)' }}>Amber — {hotspotMode.includes('COST') ? 'Cost overrun 5–15%' : 'SPI 0.85–0.95'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#22C55E', opacity: 0.85 }} />
                      <span style={{ color: 'var(--text-primary)' }}>Green — {hotspotMode.includes('COST') ? 'On budget' : 'SPI ≥ 0.95'}</span>
                    </div>
                    <div className="flex items-center gap-2 pt-1 mt-1 border-t" style={{ borderColor: 'var(--border-color)' }}>
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#94A3B8', opacity: 0.18 }} />
                      <span style={{ color: 'var(--text-muted)' }}>Faded — unlinked to CBS</span>
                    </div>
                  </div>
                </div>
              )}

              {/* KPI Overlay — as-of-today snapshot (top-center of viewer) */}
              {showKpiOverlay && stats && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-background/85 backdrop-blur-md border rounded-lg px-3 py-1 z-10 text-[10px]" data-testid="kpi-overlay">
                  <span className="uppercase tracking-wider font-semibold" style={{ color: 'var(--text-muted)' }}>As of {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                  <span>Budget <strong style={{ color: '#3B82F6' }}>{fmtCr(stats.totalBudget || 0)}</strong></span>
                  <span>Actual <strong style={{ color: '#F97316' }}>{fmtCr(stats.totalActual || 0)}</strong></span>
                  <span>Var <strong style={{ color: (stats.totalVariance || 0) >= 0 ? '#22C55E' : '#EF4444' }}>{fmtCr(stats.totalVariance || 0)}</strong></span>
                  <span>CPI <strong style={{ color: (stats.avgCpi || 0) >= 1 ? '#22C55E' : (stats.avgCpi || 0) >= 0.9 ? '#F59E0B' : '#EF4444' }}>{(stats.avgCpi || 0).toFixed(2)}</strong></span>
                  <span>SPI <strong style={{ color: (stats.avgSpi || 0) >= 1 ? '#22C55E' : (stats.avgSpi || 0) >= 0.9 ? '#F59E0B' : '#EF4444' }}>{(stats.avgSpi || 0).toFixed(2)}</strong></span>
                  <span>Objects <strong>{stats.linkedObjects || 0}/{stats.totalObjects || 0}</strong></span>
                </div>
              )}
              {selectedModel && (
                <div className="absolute top-2 right-2 bg-background/80 backdrop-blur-md border rounded-lg p-2 text-[10px] space-y-0.5 z-10" data-testid="cost-mapping-stats-overlay">
                  <div className="font-semibold text-xs mb-1">Cost Mapping Coverage</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${stats.mappingCoveragePct || 0}%` }} />
                    </div>
                    <span className="font-mono">{(stats.mappingCoveragePct || 0).toFixed(0)}%</span>
                  </div>
                  <div className="text-muted-foreground">
                    Cost Codes: {stats.mappedCostCodes || 0}/{stats.totalCostCodes || 0} | Objects: {stats.mappedModelObjects || 0}/{stats.totalModelObjects || 0}
                  </div>
                </div>
              )}
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={45} minSize={15} data-testid="fived-cost-panel">
            <Tabs value={bottomTab} onValueChange={setBottomTab} className="flex flex-col h-full">
              <TabsList className="grid w-full grid-cols-5 h-8 rounded-none shrink-0">
                <TabsTrigger value="cost-codes" className="text-xs h-7" data-testid="tab-cost-codes">CBS Cost Codes</TabsTrigger>
                <TabsTrigger value="objects" className="text-xs h-7" data-testid="tab-5d-objects">Object Tree & Properties</TabsTrigger>
                <TabsTrigger value="linking" className="text-xs h-7" data-testid="tab-cost-linking">Cost Linking</TabsTrigger>
                <TabsTrigger value="heatmap" className="text-xs h-7" data-testid="tab-heatmap">Heatmap</TabsTrigger>
                <TabsTrigger value="impact" className="text-xs h-7" data-testid="tab-impact-analyst">Impact Simulation</TabsTrigger>
              </TabsList>
              <TabsContent value="cost-codes" className="flex-1 overflow-hidden m-0">
                <CostGrid
                  mappings={mappings}
                  gridSearchText={gridSearchText}
                  onGridSearchChange={setGridSearchText}
                  onRowClicked={handleCostCodeSelect}
                  selectedCostCode={selectedCostCode}
                  onSyncCbs={handleSyncCbs}
                  onSync3dFrom4d={handleSync3dFrom4d}
                  syncing={syncing}
                  onMappingTemplate={handleMappingTemplate}
                  onMappingExport={handleMappingExport}
                  onMappingImport={handleMappingImport}
                  importingMapping={importingMapping}
                  onUnlinkAll={handleUnlinkAll}
                />
              </TabsContent>
              <TabsContent value="objects" className="flex-1 overflow-hidden m-0">
                <ObjectTreePanel
                  modelId={selectedModelId}
                  projectId={PROJECT_ID}
                  objectTreeRoots={objectTreeRoots}
                  flatObjectTreeLength={flatObjectTree.length}
                  objectTreeTotal={objectTreeTotal}
                  loadingTree={loadingTree}
                  objectTreeSearch={objectTreeSearch}
                  onSearchChange={setObjectTreeSearch}
                  onSearch={() => fetchObjectTree(objectTreeSearch)}
                  expandedTreeNodes={expandedTreeNodes}
                  onToggle={handleTreeToggle}
                  selectedTreeDbId={selectedTreeDbId}
                  onSelect={handleTreeNodeSelect}
                  highlightedTreeDbId={highlightedTreeDbId}
                  selectedObjectDetail={selectedObjectDetail}
                  selectedActivityDetail={selectedCostCodeDetail}
                  onLocateInViewer={(dbId) => viewerRef.current?.selectAndZoom(dbId)}
                />
              </TabsContent>
              <TabsContent value="linking" className="flex-1 overflow-hidden m-0">
                <CostLinkingPanel
                  modelId={selectedModelId}
                  projectId={PROJECT_ID}
                  mappings={mappings}
                  objectTreeRoots={objectTreeRoots}
                  flatObjectTreeLength={flatObjectTree.length}
                  expandedTreeNodes={expandedTreeNodes}
                  onToggle={handleTreeToggle}
                  onRefreshMappings={fetchMappings}
                />
              </TabsContent>
              <TabsContent value="heatmap" className="flex-1 overflow-hidden m-0" data-testid="tabpanel-heatmap">
                <div className="h-full flex flex-col p-4 gap-3 overflow-auto">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">3D Heatmap</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(139,92,246,0.12)', color: '#8B5CF6' }}>
                      As of {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Paint the whole model with a continuous green→yellow→red gradient based on today's cost / schedule health.
                    <br/><b>Note:</b> Only objects that are linked to a CBS cost code will be tinted — run "Sync 3D from 4D" from the CBS Cost Codes tab first, then come back here.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => toggleHotspots('GRADIENT_COST')} disabled={!viewerReady} title={!viewerReady ? 'Waiting for 3D viewer to load…' : ''} data-testid="heatmap-cost-btn">
                      Cost Creep Gradient (As of Today)
                    </Button>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => toggleHotspots('GRADIENT_SPI')} disabled={!viewerReady} title={!viewerReady ? 'Waiting for 3D viewer to load…' : ''} data-testid="heatmap-spi-btn">
                      SPI Gradient (As of Today)
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setHotspotMode(null); setHotspotCounts(null); try { viewerRef.current?.clearHotspot?.(); } catch {} }} disabled={!viewerReady} data-testid="heatmap-clear-btn">
                      Clear Heatmap
                    </Button>
                    {!viewerReady && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(148,163,184,0.15)', color: 'var(--text-muted)' }}>
                        <Loader2 className="w-3 h-3 animate-spin" /> Waiting for 3D viewer to finish loading…
                      </span>
                    )}
                    {hotspotMode?.startsWith('GRADIENT') && viewerReady && (
                      <span className="text-[10px] px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(34,197,94,0.15)', color: '#22C55E' }}>
                        Active: {hotspotMode}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Legend</div>
                    <div className="h-4 rounded-full" style={{ background: 'linear-gradient(90deg, #22C55E 0%, #EAB308 45%, #F59E0B 70%, #EF4444 100%)' }} />
                    <div className="flex justify-between text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                      <span>Healthy (0% util / SPI≥1)</span><span>Warning</span><span>Over-run</span><span>Critical</span>
                    </div>
                  </div>
                  {hotspotCounts && (
                    <div className="mt-2 rounded-lg p-3" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                      <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Painted Objects (as of today)</div>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div><span className="font-semibold" style={{ color: '#22C55E' }}>{hotspotCounts.green}</span> green</div>
                        <div><span className="font-semibold" style={{ color: '#F59E0B' }}>{hotspotCounts.amber}</span> amber</div>
                        <div><span className="font-semibold" style={{ color: '#EF4444' }}>{hotspotCounts.red}</span> red</div>
                        <div><span className="font-semibold" style={{ color: '#94A3B8' }}>{hotspotCounts.grey}</span> unlinked</div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="impact" className="flex-1 overflow-hidden m-0" data-testid="tabpanel-impact">
                <FiveDImpactAnalyst projectId={PROJECT_ID} />
              </TabsContent>
            </Tabs>
          </ResizablePanel>
        </ResizablePanelGroup>

        {/* Cost Timeliner */}
        <div className={`border-t shrink-0 transition-all duration-200 ${timelinerCollapsed ? 'h-8' : 'h-36'}`} data-testid="cost-timeliner-section">
          <div className="flex items-center justify-between px-2 h-8 border-b bg-muted/30">
            <span className="text-xs font-medium text-muted-foreground">5D Cost Timeliner</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1.5 text-[10px] gap-0.5"
              onClick={() => setTimelinerCollapsed(!timelinerCollapsed)}
              data-testid="cost-timeliner-collapse-toggle"
            >
              {timelinerCollapsed ? <ChevronsDown className="w-3 h-3" /> : <ChevronsUp className="w-3 h-3" />}
              {timelinerCollapsed ? 'Expand' : 'Collapse'}
            </Button>
          </div>
          {!timelinerCollapsed && (
            <FiveDTimeliner
              snapshots={snapshots}
              currentIndex={snapshotIndex}
              onIndexChange={setSnapshotIndex}
              isPlaying={isPlaying}
              onTogglePlay={() => setIsPlaying(!isPlaying)}
              playSpeed={playSpeed}
              onSpeedChange={setPlaySpeed}
            />
          )}
        </div>
      </div>

      <UploadDialog open={showUploadDialog} onOpenChange={setShowUploadDialog} onSubmit={handleUpload} uploading={uploading} />
    </DashboardLayout>
  );
}

// Toolbar
function FiveDToolbar({
  models, selectedModelId, setSelectedModelId, selectedModel,
  vizMode, setVizMode, showLegend, setShowLegend, showKpiOverlay, setShowKpiOverlay, isFullscreen, setIsFullscreen,
  syncing, generating, onUpload, onLinkCbs, onAutoMap, onSyncCbs, onSyncCbsFromSchedule, onGenerateSnapshots, onBack, onDeleteModel
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b flex-wrap" data-testid="fived-toolbar">
      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={onBack} data-testid="fived-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" />
      </Button>
      <div className="flex items-center gap-1.5">
        <DollarSign className="w-4 h-4 text-cyan-500" />
        <span className="text-sm font-semibold">5D Cost Modelling</span>
      </div>
      <Select value={selectedModelId || ''} onValueChange={setSelectedModelId}>
        <SelectTrigger className="h-7 w-48 text-xs" data-testid="5d-model-selector">
          <SelectValue placeholder="Select Model" />
        </SelectTrigger>
        <SelectContent>
          {models.map(m => <SelectItem key={m.modelId} value={m.modelId}>{m.modelName}{m._source === '4D' ? ' (4D)' : ''}</SelectItem>)}
          {models.length === 0 && <SelectItem value="none" disabled>No models</SelectItem>}
        </SelectContent>
      </Select>
      {selectedModelId && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={onDeleteModel} data-testid="5d-delete-model-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete Model</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {selectedModel?.linkedCbsName && (
        <Badge variant="secondary" className="text-[10px] h-6">
          <Link2 className="w-3 h-3 mr-1" />{selectedModel.linkedCbsName}
        </Badge>
      )}
      <TranslationBadge status={selectedModel?.translationStatus} urn={selectedModel?.apsModelUrn} deriv={selectedModel?.apsDerivativeUrn} />
      <Select value={vizMode} onValueChange={setVizMode}>
        <SelectTrigger className="h-7 w-44 text-xs" data-testid="5d-viz-mode-selector">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="BUDGET_VS_ACTUAL">Budget vs Actual</SelectItem>
          <SelectItem value="COST_DISTRIBUTION">Cost Distribution</SelectItem>
          <SelectItem value="EVM_OBJECTS">EVM on Objects</SelectItem>
          <SelectItem value="COST_TIMELINE">Cost Timeline</SelectItem>
        </SelectContent>
      </Select>
      <div className="ml-auto flex items-center gap-1">
        <TooltipProvider>
          <ToolbarBtn icon={Upload} tip="Upload 3D Model" onClick={onUpload} testId="5d-upload-btn" />
          <ToolbarBtn icon={Link2} tip="Link CBS" onClick={onLinkCbs} disabled={syncing} testId="5d-link-cbs-btn" />
          <ToolbarBtn icon={Wand2} tip="Auto-Map Objects" onClick={onAutoMap} disabled={syncing} testId="5d-auto-map-btn" />
          <ToolbarBtn icon={RefreshCw} tip="Sync CBS from CBS Matrix (SSOT)" onClick={onSyncCbs} disabled={syncing} spin={syncing} testId="5d-sync-cbs-btn" />
          <ToolbarBtn icon={DollarSign} tip="Generate Cost Snapshots" onClick={onGenerateSnapshots} disabled={generating} pulse={generating} testId="5d-generate-btn" />
          <ToolbarBtn icon={Eye} tip="Legend" onClick={() => setShowLegend(!showLegend)} testId="5d-legend-btn" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant={showKpiOverlay ? 'default' : 'ghost'} size="sm" className="h-7 px-2 gap-1 text-[10px]" onClick={() => setShowKpiOverlay(!showKpiOverlay)} data-testid="5d-toggle-kpi-overlay-btn">
                <Gauge className="w-3.5 h-3.5" /> KPI Overlay
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle KPI Overlay (as of today)</TooltipContent>
          </Tooltip>
          <ToolbarBtn icon={Maximize2} tip="Fullscreen" onClick={() => setIsFullscreen(!isFullscreen)} testId="5d-fullscreen-btn" />
        </TooltipProvider>
      </div>
    </div>
  );
}

function ToolbarBtn({ icon: Icon, tip, onClick, disabled, spin, pulse, testId }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClick} disabled={disabled} data-testid={testId}>
          <Icon className={`w-3.5 h-3.5 ${spin ? 'animate-spin' : ''} ${pulse ? 'animate-pulse' : ''}`} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

function TranslationBadge({ status, urn, deriv }) {
  if (status === 'TRANSLATING') return <Badge variant="outline" className="text-[10px] h-6 border-blue-500 text-blue-400" data-testid="5d-translation-status"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Translating...</Badge>;
  if (status === 'COMPLETE' && deriv && urn !== 'demo-urn-no-real-model') return <Badge variant="outline" className="text-[10px] h-6 border-emerald-500 text-emerald-400" data-testid="5d-translation-status"><CheckCircle2 className="w-3 h-3 mr-1" />3D Ready</Badge>;
  if (status === 'FAILED') return <Badge variant="outline" className="text-[10px] h-6 border-red-500 text-red-400" data-testid="5d-translation-status"><AlertTriangle className="w-3 h-3 mr-1" />Failed</Badge>;
  if (status === 'DEMO') return <Badge variant="outline" className="text-[10px] h-6 border-slate-500 text-slate-400" data-testid="5d-translation-status"><Box className="w-3 h-3 mr-1" />Demo</Badge>;
  return null;
}

// Upload Dialog
function UploadDialog({ open, onOpenChange, onSubmit, uploading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="5d-upload-dialog">
        <DialogHeader><DialogTitle>Upload 3D Model for Cost Visualization</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-xs">Model Name</Label>
            <Input name="model_name" defaultValue="Plant 3D Model" className="mt-1" data-testid="5d-model-name-input" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input name="model_description" placeholder="Optional" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">3D Model File</Label>
            <Input name="file" type="file" accept=".rvt,.nwd,.ifc,.dwg,.stp,.3dm,.zip" className="mt-1" required data-testid="5d-model-file-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={uploading} data-testid="5d-upload-submit-btn">{uploading ? 'Uploading...' : 'Upload & Translate'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
